import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Box, Button, Chip, CircularProgress, Link, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
    TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../../context/ToastContext';
import { AuthService } from '../../../services/auth.service';
import { B2bAccountingService } from '../../../services/b2bAccounting.service';
import { B2bMaturitySchedule as Schedule, B2bMaturityTotal } from '../../../interfaces/commercial.interface';
import { formatMoney } from '../../../utils/format';
import { formatDate } from '../../../utils/dateUtils';
import { usePermission } from '../../../hooks/usePermission';
import { PERMISSIONS } from '../../../constants/permissions';
import RetireByLoanDialog, { RetireTarget } from '../../financing/RetireByLoanDialog'; // W3-B5

const foreignText = (t: B2bMaturityTotal) =>
    Object.entries(t.foreignByCurrency).map(([c, v]) => formatMoney(v, c)).join(' + ') || '—';

const TotalsTable: React.FC<{ title: string; rows: B2bMaturityTotal[]; labelHeader: string }> = ({ title, rows, labelHeader }) => (
    <Paper data-help-section="commercialReports.b2b.totals" sx={{ p: 2, flex: '1 1 320px', minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>{title}</Typography>
        <TableContainer>
            <Table size="small">
                <TableHead>
                    <TableRow>
                        <TableCell data-help="commercialReports.b2b.col.group">{labelHeader}</TableCell>
                        <TableCell data-help="commercialReports.b2b.col.count" align="right">Count</TableCell>
                        <TableCell data-help="commercialReports.b2b.col.foreign" align="right">Foreign</TableCell>
                        <TableCell data-help="commercialReports.b2b.col.bdt" align="right">BDT liability</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map(t => (
                        <TableRow key={t.key}>
                            <TableCell>{t.key === 'OVERDUE' ? <Chip size="small" color="error" label="Overdue" /> : t.key}</TableCell>
                            <TableCell align="right">{t.count}</TableCell>
                            <TableCell align="right">{foreignText(t)}</TableCell>
                            <TableCell align="right">{formatMoney(t.outstandingBase, 'BDT')}</TableCell>
                        </TableRow>
                    ))}
                    {rows.length === 0 && <TableRow><TableCell colSpan={4} align="center">Nothing open.</TableCell></TableRow>}
                </TableBody>
            </Table>
        </TableContainer>
    </Paper>
);

/** B2B maturity schedule (accounting wave 2): open bank acceptances by days to maturity, bank and supplier. */
const B2bMaturityScheduleReport: React.FC = () => {
    const toast = useToast();
    const institutionId = AuthService.getSelectedInstitution() ?? '';
    const [asOf, setAsOf] = useState('');
    const [data, setData] = useState<Schedule | null>(null);
    const [loading, setLoading] = useState(true);
    const canRetire = usePermission(PERMISSIONS.FINANCING_MANAGE); // W3-B5
    const [retire, setRetire] = useState<RetireTarget | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        if (!institutionId) return;
        (async () => {
            try {
                setLoading(true);
                setData(await B2bAccountingService.getMaturitySchedule(institutionId, asOf || undefined));
            } catch (e) {
                toast.showApiError(e, 'Failed to load B2B maturity schedule');
            } finally {
                setLoading(false);
            }
        })();
    }, [institutionId, asOf, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box data-help-page="commercialReports.b2b.page" sx={{ p: 3 }}>
            <Typography variant="h5" sx={{ mb: 2 }}>B2B Maturity Schedule</Typography>
            <Box data-help-section="commercialReports.b2b.filters" sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <TextField data-help="commercialReports.b2b.asOf" label="As of" type="date" size="small" value={asOf} InputLabelProps={{ shrink: true }}
                    helperText="Empty = today" onChange={e => setAsOf(e.target.value)} sx={{ flex: '1 1 200px', maxWidth: 260 }} />
            </Box>
            {loading || !data ? <CircularProgress /> : (
                <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        As of {formatDate(data.asOf)}: {formatMoney(data.totalOutstandingBase, 'BDT')} open liability,
                        of which {formatMoney(data.overdueOutstandingBase, 'BDT')} overdue.
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 3 }}>
                        <TotalsTable title="By maturity (days)" rows={data.byBucket} labelHeader="Bucket" />
                        <TotalsTable title="By issuing bank" rows={data.byBank} labelHeader="Bank" />
                        <TotalsTable title="By supplier" rows={data.bySupplier} labelHeader="Supplier" />
                    </Box>
                    <TableContainer data-help-section="commercialReports.b2b.table" component={Paper}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell data-help="commercialReports.b2b.col.lc">B2B LC</TableCell>
                                    <TableCell data-help="commercialReports.b2b.col.bill">Bill</TableCell>
                                    <TableCell data-help="commercialReports.b2b.col.bank">Bank</TableCell>
                                    <TableCell data-help="commercialReports.b2b.col.supplier">Supplier</TableCell>
                                    <TableCell data-help="commercialReports.b2b.col.maturity">Maturity</TableCell>
                                    <TableCell data-help="commercialReports.b2b.col.days" align="right">Days</TableCell>
                                    <TableCell data-help="commercialReports.b2b.col.outstanding" align="right">Outstanding</TableCell>
                                    <TableCell data-help="commercialReports.b2b.col.bdt" align="right">BDT liability</TableCell>
                                    {canRetire && <TableCell data-help="commercialReports.b2b.col.action" align="right">Action</TableCell>}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {data.rows.map(r => (
                                    <TableRow key={r.acceptanceId}>
                                        <TableCell>
                                            <Link data-help="commercialReports.b2b.action.openLc" component={RouterLink} to={`/commercial/back-to-back-lc/${r.masterLcId}/${r.backToBackLcId}`}>{r.b2bLcNumber}</Link>
                                        </TableCell>
                                        <TableCell>{r.billReference}</TableCell>
                                        <TableCell>{r.issuingBank}</TableCell>
                                        <TableCell>{r.supplierName}</TableCell>
                                        <TableCell>{formatDate(r.maturityDate)}</TableCell>
                                        <TableCell align="right">{r.daysToMaturity < 0 ? <Chip size="small" color="error" label={`${-r.daysToMaturity} overdue`} /> : r.daysToMaturity}</TableCell>
                                        <TableCell align="right">{formatMoney(r.outstandingForeign, r.currencyCode)}</TableCell>
                                        <TableCell align="right">{formatMoney(r.outstandingBase, 'BDT')}</TableCell>
                                        {canRetire && (
                                            <TableCell align="right">
                                                {r.padCandidate && (
                                                    <Button data-help="commercialReports.b2b.action.retire" size="small" data-testid={`retire-by-loan-${r.acceptanceId}`}
                                                        onClick={() => setRetire({
                                                            acceptanceId: r.acceptanceId, label: `${r.b2bLcNumber} ${r.billReference}`,
                                                            currencyCode: r.currencyCode, outstandingForeign: r.outstandingForeign,
                                                        })}>Retire by loan</Button>
                                                )}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                {data.rows.length === 0 && <TableRow><TableCell colSpan={8 + (canRetire ? 1 : 0)} align="center">No open acceptances.</TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </>
            )}
            <RetireByLoanDialog open={!!retire} institutionId={institutionId} target={retire}
                onClose={() => setRetire(null)} onDone={() => { setRetire(null); setReloadKey(k => k + 1); }} />
        </Box>
    );
};

export default B2bMaturityScheduleReport;
